import { useEffect, useRef, useState } from 'react';
import './living-portrait.css';
import { renderPortraitVideo } from './portraitRenderer';

export function LivingPortraitHero() {
  const video = useRef<HTMLVideoElement>(null);
  const stage = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const [motion, setMotion] = useState(false);
  const [visible, setVisible] = useState(true);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const [composited, setComposited] = useState(false);

  useEffect(() => {
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updatePreference = () => setMotion(!preference.matches);
    updatePreference();
    preference.addEventListener('change', updatePreference);
    let inView = true;
    const updateVisibility = () => setVisible(inView && !document.hidden);
    document.addEventListener('visibilitychange', updateVisibility);
    const observer = new IntersectionObserver(([entry]) => { inView = entry.isIntersecting; updateVisibility(); }, { threshold: 0.05 });
    if (stage.current) observer.observe(stage.current);
    return () => { preference.removeEventListener('change', updatePreference); document.removeEventListener('visibilitychange', updateVisibility); observer.disconnect(); };
  }, []);

  useEffect(() => {
    if (!video.current) return;
    if (motion && visible && !failed) {
      void video.current.play().catch(error => { if (error?.name !== 'AbortError') setMotion(false); });
    } else video.current.pause();
  }, [motion, visible, failed]);

  useEffect(() => {
    if (!ready || failed || !canvas.current || !video.current) return;
    try {
      const stop = renderPortraitVideo(canvas.current, video.current);
      setComposited(true);
      return stop;
    } catch { setFailed(true); setMotion(false); }
  }, [ready, failed]);

  return <section className="tim-living-hero" aria-labelledby="campaign-title">
    <div className="tim-living-stage" ref={stage}>
      <div className="tim-living-panels" aria-hidden="true"><span /><span /><span /></div>
      <p className="tim-living-kicker">THREE FACES. A THOUSAND STORIES. ONE TRUTH.</p>
      <h1 id="campaign-title" className="tim-living-type"><span>I AM NOT</span><span>MY DIAGNOSIS.</span><em>I am still me.</em></h1>
      <div className="tim-living-people" aria-hidden="true">
        <img className={`tim-living-still ${composited && !failed ? 'is-replaced' : ''}`} src="/images/this-is-me/people-cutout.png" alt="" width="1536" height="1024" />
        {!failed && <><video ref={video} className="tim-living-source" src="/images/this-is-me/living-portraits.mp4" muted loop playsInline preload="metadata" onPlaying={() => setReady(true)} onError={() => { setFailed(true); setMotion(false); }} /><canvas ref={canvas} className={`tim-living-video ${composited ? 'is-ready' : ''}`} /></>}
      </div>
      <p className="tim-visually-hidden">Three imagined people in pink, gold, and violet, with gentle natural movement. AI-created campaign portraits, not client testimonials.</p>
      <div className="tim-living-labels" aria-hidden="true"><span>01 / STILL BLOOMING</span><span>02 / STILL DREAMING</span><span>03 / STILL BECOMING</span></div>
      <div className="tim-living-controls"><span>AI-CREATED CAMPAIGN PORTRAITS</span>{!failed && <button type="button" onClick={() => setMotion(value => !value)} aria-pressed={motion}>{motion ? 'Ⅱ Pause motion' : '▷ Play motion'}</button>}</div>
    </div>
    <div className="tim-living-invitation">
      <div className="tim-invitation-title">
        <p className="tim-eyebrow">CMJ CINEMA × ETDS DETROIT PRESENT</p>
        <h2>Seen through art.<br /><em>Held with care.</em></h2>
        <p className="tim-invitation-campaign">THIS IS ME <span>— A SHARED INVITATION</span></p>
      </div>
      <div className="tim-invitation-copy">
        <p>CMJ Cinema’s portrait and film storytelling meets ETDS Detroit’s spirit of thoughtful care. Together, we’re making space for the person beyond a diagnosis.</p>
        <p>You bring your story. We celebrate the whole of you.</p>
        <div className="tim-invitation-actions"><a href="#experiences" className="tim-button tim-button-pink">Find your experience <span aria-hidden="true">↗</span></a><a className="tim-partner-discover" href="https://etdsdetroit.com/" target="_blank" rel="noopener noreferrer">Discover ETDS Detroit <span aria-hidden="true">↗</span><span className="tim-visually-hidden"> (opens in a new tab)</span></a></div>
      </div>
    </div>
  </section>;
}
